import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchCategoryBySlug } from "@/apis";
import { fetchUserAccess, useUserStore } from "@/store/slices";
import { findUserAccess, getUserAccessCategoryId } from "@/utility";

const getCategory = (response) => {
    const rawCategory = response?.data?.response ?? response?.data;
    if (Array.isArray(rawCategory)) return rawCategory[0];
    return rawCategory?.category ?? rawCategory;
};

const getCategoryId = (category) => {
    const categoryId = category?.id ?? category?.categoryId ?? category?.category_id;
    return categoryId === null || categoryId === undefined
        ? ""
        : String(categoryId);
};

const useOnboardingCategory = ({
    categoryKey,
    userAccessId,
    fallbackCategoryId = "",
}) => {
    const dispatch = useDispatch();
    const {
        userAccessList,
        userAccessLoaded,
        userAccessLoading,
    } = useUserStore();
    const [resolvedCategory, setResolvedCategory] = useState(null);
    const [isResolvingCategory, setResolvingCategory] = useState(
        Boolean(categoryKey)
    );

    useEffect(() => {
        if (!userAccessLoaded && !userAccessLoading) {
            dispatch(fetchUserAccess());
        }
    }, [dispatch, userAccessLoaded, userAccessLoading]);

    const storedUserAccess = useMemo(
        () => findUserAccess(userAccessList, userAccessId, categoryKey),
        [categoryKey, userAccessId, userAccessList]
    );
    const storedCategoryId = getUserAccessCategoryId(storedUserAccess);

    useEffect(() => {
        if (!categoryKey || storedCategoryId) {
            setResolvedCategory(null);
            setResolvingCategory(false);
            return;
        }

        let active = true;
        setResolvingCategory(true);
        fetchCategoryBySlug(categoryKey)
            .then((response) => {
                if (active) setResolvedCategory(getCategory(response));
            })
            .catch(() => {
                if (active) setResolvedCategory(null);
            })
            .finally(() => {
                if (active) setResolvingCategory(false);
            });

        return () => {
            active = false;
        };
    }, [categoryKey, storedCategoryId]);

    return {
        category: resolvedCategory,
        categoryId:
            storedCategoryId ||
            getCategoryId(resolvedCategory) ||
            String(fallbackCategoryId || ""),
        isResolvingCategory,
    };
};

export default useOnboardingCategory;
